import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import Plot from "react-plotly.js";

import Card from "components/atoms/Card";

export type UsageRoute = {
  routeKey: string;
  displayName: string;
  category: string;
  description?: string;
  totalCalls: number;
  totalTokens: number;
  avgDailyCalls: number;
  avgDailyTokens: number;
  avgTokensPerCall?: number;
  totalCostUSD?: number;
  avgDailyCostUSD?: number;
  costPerCall?: number;
};

export type UsageBillingContext = {
  includedTokens?: number;
  currentUsageTokens?: number;
  projectedTokens?: number;
  pricePerMillion?: number;
  refillBatchTokens?: number;
  projectionLabel?: string;
};

const FAMILY_COLORS: Record<string, string> = {
  Vision: "#16a34a",
  "Text AI": "#ea580c",
  Search: "#2563eb",
  "Food Database": "#0891b2",
  "Advisor / Chat": "#7c3aed",
  Catalog: "#db2777",
  Affiliates: "#0f766e",
  Other: "#6b7280",
};

const MONTH_DAYS = 30.44;
const CHART_LIMIT = 20;

type SortKey =
  | "name"
  | "calls"
  | "tokens"
  | "share"
  | "perCall"
  | "periodCost"
  | "projected";

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  if (num === 0) return "0";
  if (num < 1) return num.toFixed(2);
  return num.toFixed(0);
};

const formatExact = (num: number): string =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.round(num)
  );

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount) || amount === 0) return "$0.00";
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
};

const formatPct = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 0.1) return "<0.1%";
  return `${value.toFixed(1)}%`;
};

export const routeFamily = (category: string, routeKey = ""): string => {
  const haystack = `${category} ${routeKey}`.toLowerCase();
  if (haystack.includes("vision")) return "Vision";
  if (haystack.includes("semantic") || haystack.includes("search"))
    return "Search";
  if (haystack.includes("catalog") || haystack.includes("chain"))
    return "Catalog";
  if (haystack.includes("affiliate")) return "Affiliates";
  if (
    haystack.includes("advisor") ||
    haystack.includes("chat") ||
    haystack.includes("easyai")
  )
    return "Advisor / Chat";
  if (haystack.includes("food")) return "Food Database";
  if (haystack.includes("ai tool") || haystack.includes("ai tools"))
    return "Text AI";
  return "Other";
};

type Props = {
  routes: UsageRoute[];
  days: number;
  tokenLabel?: string;
  billing?: UsageBillingContext;
};

const RouteUsageOverview = ({
  routes,
  days,
  tokenLabel = "tokens",
  billing,
}: Props) => {
  const [metric, setMetric] = useState<"calls" | "tokens">("tokens");
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("tokens");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const pricePerMillion = billing?.pricePerMillion || 0;
  const includedTokens = billing?.includedTokens || 0;
  const projectedTokens = billing?.projectedTokens || 0;
  const refillBatchTokens = billing?.refillBatchTokens || 0;

  const used = useMemo(
    () =>
      routes.filter((route) => route.totalCalls > 0 || route.totalTokens > 0),
    [routes]
  );

  const families = useMemo(() => {
    const map = new Map<
      string,
      { calls: number; tokens: number; routes: number; cost: number }
    >();
    used.forEach((route) => {
      const family = routeFamily(route.category, route.routeKey);
      const current = map.get(family) || {
        calls: 0,
        tokens: 0,
        routes: 0,
        cost: 0,
      };
      current.calls += route.totalCalls;
      current.tokens += route.totalTokens;
      current.cost += route.totalCostUSD || 0;
      current.routes += 1;
      map.set(family, current);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [used]);

  const totalCalls = used.reduce((sum, route) => sum + route.totalCalls, 0);
  const totalTokens = used.reduce((sum, route) => sum + route.totalTokens, 0);
  const totalCost = used.reduce(
    (sum, route) => sum + (route.totalCostUSD || 0),
    0
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = used.filter((route) => {
      const family = routeFamily(route.category, route.routeKey);
      if (familyFilter && family !== familyFilter) return false;
      if (!needle) return true;
      return (
        route.displayName.toLowerCase().includes(needle) ||
        route.routeKey.toLowerCase().includes(needle) ||
        family.toLowerCase().includes(needle) ||
        (route.description || "").toLowerCase().includes(needle) ||
        (route.category || "").toLowerCase().includes(needle)
      );
    });

    const projectedCost = (route: UsageRoute) =>
      pricePerMillion > 0
        ? (route.avgDailyTokens * MONTH_DAYS * pricePerMillion) / 1_000_000
        : (route.avgDailyCostUSD || 0) * MONTH_DAYS;

    const valueOf = (route: UsageRoute): number | string => {
      switch (sortKey) {
        case "name":
          return route.displayName.toLowerCase();
        case "calls":
          return route.totalCalls;
        case "share":
          return totalTokens > 0 ? route.totalTokens / totalTokens : 0;
        case "perCall":
          return route.avgTokensPerCall || 0;
        case "periodCost":
          return route.totalCostUSD || 0;
        case "projected":
          return projectedCost(route);
        case "tokens":
        default:
          return route.totalTokens;
      }
    };

    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc"
        ? Number(av) - Number(bv)
        : Number(bv) - Number(av);
    });
  }, [
    used,
    search,
    familyFilter,
    sortKey,
    sortDir,
    totalTokens,
    pricePerMillion,
  ]);

  const chartRows = filtered.slice(0, CHART_LIMIT);
  const familyValues = families.map((family) =>
    metric === "tokens" ? family.tokens : family.calls
  );
  const includedUsedPct =
    includedTokens > 0
      ? ((billing?.currentUsageTokens || totalTokens) / includedTokens) * 100
      : 0;
  const projectedOverage = Math.max(0, projectedTokens - includedTokens);
  const projectedRefills =
    refillBatchTokens > 0 ? Math.ceil(projectedOverage / refillBatchTokens) : 0;
  const refillRateCost =
    pricePerMillion > 0 ? (totalTokens / 1_000_000) * pricePerMillion : 0;
  const projectedMonthCost =
    pricePerMillion > 0
      ? ((totalTokens / Math.max(days, 1)) * MONTH_DAYS * pricePerMillion) /
        1_000_000
      : 0;

  const toggleSort = (next: SortKey) => {
    if (sortKey === next) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(next);
    setSortDir(next === "name" ? "asc" : "desc");
  };

  const SortHeader = ({
    id,
    label,
    align = "right",
  }: {
    id: SortKey;
    label: string;
    align?: "left" | "right";
  }) => (
    <th
      className={`py-2 px-3 cursor-pointer select-none ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={() => toggleSort(id)}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "justify-end" : ""
        }`}
      >
        {label}
        {sortKey === id &&
          (sortDir === "asc" ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          ))}
      </span>
    </th>
  );

  if (used.length === 0) {
    return (
      <Card className="p-6">
        <h4 className="font-semibold text-gray-900">Usage by route</h4>
        <p className="mt-2 text-sm text-gray-500">
          No API calls in this period yet. Usage by vision, text, search, and
          other groups will show up here after the first requests.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h4 className="font-semibold text-gray-900">Usage by route</h4>
          <p className="text-sm text-gray-500">
            Every route this account called in the selected period. Search or
            click a group to find a specific API. Path IDs are collapsed.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "calls"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("calls")}
          >
            Calls
          </button>
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "tokens"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("tokens")}
          >
            {tokenLabel}
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            This period
          </p>
          <p className="text-xl font-semibold text-gray-900">
            {formatNumber(totalTokens)}
          </p>
          <p className="text-xs text-gray-500">
            {formatExact(totalCalls)} calls across {used.length} routes
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Included budget
          </p>
          <p className="text-xl font-semibold text-gray-900">
            {includedTokens > 0 ? formatPct(includedUsedPct) : "—"}
          </p>
          <p className="text-xs text-gray-500">
            {includedTokens > 0
              ? `${formatNumber(
                  billing?.currentUsageTokens || totalTokens
                )} of ${formatNumber(includedTokens)} included`
              : "No included-token allotment on this plan"}
          </p>
        </div>
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-indigo-700">
            {billing?.projectionLabel || "30-day projection"}
          </p>
          <p className="text-xl font-semibold text-indigo-950">
            {projectedTokens > 0
              ? formatNumber(projectedTokens)
              : formatNumber((totalTokens / Math.max(days, 1)) * MONTH_DAYS)}
          </p>
          <p className="text-xs text-indigo-800">
            {includedTokens > 0
              ? projectedOverage > 0
                ? `${formatNumber(projectedOverage)} over included${
                    projectedRefills > 0
                      ? ` · ${projectedRefills} refill batch${
                          projectedRefills === 1 ? "" : "es"
                        }`
                      : ""
                  }`
                : "On pace to stay within included tokens"
              : `${formatCurrency(projectedMonthCost)} at refill rate`}
          </p>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-amber-800">
            Refill-rate value
          </p>
          <p className="text-xl font-semibold text-amber-950">
            {formatCurrency(totalCost || refillRateCost)}
          </p>
          <p className="text-xs text-amber-800">
            {pricePerMillion > 0
              ? `What this period would cost at $${pricePerMillion.toFixed(
                  2
                )}/M if it were all overage. Actual billing starts after included tokens.`
              : "Reference cost from analytics pricing"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find a route: berg, vision, search, grocery..."
            className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-full bg-white"
          />
        </div>
        {familyFilter && (
          <button
            type="button"
            className="text-xs font-medium text-indigo-700 hover:underline"
            onClick={() => setFamilyFilter(null)}
          >
            Clear group filter
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {families.map((family) => {
          const active = familyFilter === family.name;
          return (
            <button
              key={family.name}
              type="button"
              onClick={() =>
                setFamilyFilter((prev) =>
                  prev === family.name ? null : family.name
                )
              }
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                active
                  ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor:
                    FAMILY_COLORS[family.name] || FAMILY_COLORS.Other,
                }}
              />
              {family.name}
              <span className="text-gray-500">
                {formatNumber(
                  metric === "tokens" ? family.tokens : family.calls
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">
            By product group
          </p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            Full-account mix. Click a chip above to focus the table.
          </p>
          <Plot
            data={[
              {
                type: "pie",
                hole: 0.45,
                labels: families.map((family) => family.name),
                values: familyValues,
                marker: {
                  colors: families.map(
                    (family) =>
                      FAMILY_COLORS[family.name] || FAMILY_COLORS.Other
                  ),
                },
                textinfo: "label+percent",
                textposition: "inside",
                insidetextorientation: "horizontal",
                hovertemplate:
                  "%{label}<br>%{value:,} " +
                  metric +
                  "<br>%{percent}<extra></extra>",
              },
            ]}
            layout={{
              height: 300,
              margin: { t: 8, r: 8, b: 8, l: 8 },
              paper_bgcolor: "transparent",
              showlegend: false,
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: "100%", height: "300px" }}
            onClick={(event) => {
              const pointNumber = event?.points?.[0]?.pointNumber;
              const label =
                typeof pointNumber === "number"
                  ? families[pointNumber]?.name
                  : undefined;
              if (label) {
                setFamilyFilter((prev) => (prev === label ? null : label));
              }
            }}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">
            {familyFilter || search.trim()
              ? "Matching routes"
              : "Largest routes"}
          </p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            {filtered.length <= CHART_LIMIT
              ? `All ${filtered.length} matching routes.`
              : `Charting the top ${CHART_LIMIT} of ${filtered.length} matches. The table below has every row.`}
          </p>
          {chartRows.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              No routes match that search.
            </p>
          ) : (
            <Plot
              data={[
                {
                  type: "bar",
                  orientation: "h",
                  y: chartRows.map((route) =>
                    route.displayName.length > 28
                      ? `${route.displayName.slice(0, 28)}…`
                      : route.displayName
                  ),
                  x: chartRows.map((route) =>
                    metric === "tokens" ? route.totalTokens : route.totalCalls
                  ),
                  marker: {
                    color: chartRows.map(
                      (route) =>
                        FAMILY_COLORS[
                          routeFamily(route.category, route.routeKey)
                        ] || FAMILY_COLORS.Other
                    ),
                  },
                  customdata: chartRows.map((route) => [
                    route.displayName,
                    routeFamily(route.category, route.routeKey),
                    route.totalCalls,
                    route.totalTokens,
                    route.avgDailyCalls,
                    route.totalCostUSD || 0,
                  ]),
                  hovertemplate:
                    "%{customdata[0]}<br>%{customdata[1]}<br>Calls: %{customdata[2]:,}<br>Tokens: %{customdata[3]:,}<br>Avg/day: %{customdata[4]:.1f}<br>Period $: %{customdata[5]:$.2f}<extra></extra>",
                },
              ]}
              layout={{
                height: Math.max(300, 28 * chartRows.length + 60),
                margin: { t: 8, r: 16, b: 32, l: 140 },
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                yaxis: { autorange: "reversed", tickfont: { size: 11 } },
                xaxis: {
                  title: metric === "tokens" ? tokenLabel : "Calls",
                  rangemode: "tozero",
                },
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{
                width: "100%",
                height: `${Math.max(300, 28 * chartRows.length + 60)}px`,
              }}
            />
          )}
        </div>
      </div>

      <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900">
            All matching calls
          </p>
          <p className="text-xs text-gray-500">
            {filtered.length} route{filtered.length === 1 ? "" : "s"} · share is
            of this period&apos;s {tokenLabel.toLowerCase()} · 30d $ is this
            route&apos;s current daily pace at refill rate
          </p>
        </div>
        <div className="overflow-auto max-h-[480px]">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="border-b border-gray-200 text-xs text-gray-600">
                <SortHeader id="name" label="Route" align="left" />
                <th className="py-2 px-3 text-left">Group</th>
                <SortHeader id="calls" label="Calls" />
                <SortHeader id="tokens" label="Tokens" />
                <SortHeader id="share" label="Share" />
                <SortHeader id="perCall" label="Tok/call" />
                <SortHeader id="periodCost" label="Period $" />
                <SortHeader id="projected" label="30d proj $" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    No routes match. Try another name, group, or clear the
                    filter.
                  </td>
                </tr>
              ) : (
                filtered.map((route) => {
                  const family = routeFamily(route.category, route.routeKey);
                  const share =
                    totalTokens > 0
                      ? (route.totalTokens / totalTokens) * 100
                      : 0;
                  const projected =
                    pricePerMillion > 0
                      ? (route.avgDailyTokens * MONTH_DAYS * pricePerMillion) /
                        1_000_000
                      : (route.avgDailyCostUSD || 0) * MONTH_DAYS;
                  return (
                    <tr
                      key={route.routeKey}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2 px-3">
                        <p className="font-medium text-gray-900">
                          {route.displayName}
                        </p>
                        {route.description && (
                          <p className="text-[11px] text-gray-500 line-clamp-1">
                            {route.description}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{
                              backgroundColor:
                                FAMILY_COLORS[family] || FAMILY_COLORS.Other,
                            }}
                          />
                          {family}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatExact(route.totalCalls)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(route.totalTokens)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatPct(share)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-gray-600">
                        {formatNumber(route.avgTokensPerCall || 0)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(route.totalCostUSD || 0)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-indigo-800">
                        {formatCurrency(projected)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

export default RouteUsageOverview;
